import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { FolderPlus, X } from 'lucide-react';

/**
 * Shared bits so EVERY "collection" dropdown across the app behaves the same:
 * the list scrolls within a capped height, and the "Add collection" action looks
 * and sits identically. Change the scroll height or the button once, here.
 */
export function CollectionOptions({ children }) {
    // Wrap a set of collection <SelectItem>s so a long list scrolls on its own
    // and any pinned header/footer around it stays visible.
    return <div className="max-h-[45vh] overflow-y-auto">{children}</div>;
}

export function AddCollectionButton({ onClick, testId }) {
    return (
        <div className="p-1 border-t border-stone-100 bg-popover">
            <button
                type="button"
                data-testid={testId}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-sm font-medium text-primary rounded-sm hover:bg-stone-100"
                onClick={onClick}
            >
                <FolderPlus className="h-4 w-4" /> Add collection
            </button>
        </div>
    );
}

/**
 * Shared "assign to collection" dropdown with an inline "Add collection" entry
 * at the bottom of the list. Used anywhere a phrase/generation is assigned to a
 * collection (Add Phrase, AI Generate, …) so the behaviour lives in ONE place.
 *
 * Selecting "Add collection" closes the dropdown and reveals a name field in
 * its place (kept OUTSIDE the Radix Select so typing works on Android). On
 * create, the new collection is auto-selected.
 *
 * Props:
 *  - value: selected collection id, or '' for none
 *  - onChange(id): '' when "No collection"
 *  - collections: [{ id, name }]
 *  - onCreateCollection(name) => Promise<collection|null>: creates + returns it
 *  - placeholder / triggerClassName / testId: optional
 */
export default function CollectionSelect({
    value,
    onChange,
    collections,
    onCreateCollection,
    placeholder = 'No collection',
    triggerClassName = 'bg-stone-50',
    testId,
}) {
    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [name, setName] = useState('');

    const submit = async () => {
        if (!name.trim()) return;
        const created = await onCreateCollection(name.trim());
        if (created) {
            onChange(created.id);
            setName('');
            setCreating(false);
        }
    };

    if (creating) {
        return (
            <div className="flex items-center gap-1.5">
                <Input
                    autoFocus
                    data-testid={testId ? `${testId}-new-input` : undefined}
                    value={name}
                    onChange={e => setName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
                    placeholder="New collection name"
                    className="bg-stone-50 flex-1"
                />
                <Button type="button" size="sm" className="rounded-md bg-primary hover:bg-primary/80 shrink-0" onClick={submit}>Create</Button>
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-stone-400 hover:text-stone-600 shrink-0" onClick={() => { setCreating(false); setName(''); }}>
                    <X className="h-4 w-4" />
                </Button>
            </div>
        );
    }

    return (
        <Select open={open} onOpenChange={setOpen} value={value || 'none'} onValueChange={v => onChange(v === 'none' ? '' : v)}>
            <SelectTrigger className={triggerClassName} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
            <SelectContent>
                <SelectItem value="none">No collection</SelectItem>
                <CollectionOptions>
                    {collections.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </CollectionOptions>
                <AddCollectionButton
                    testId={testId ? `${testId}-add-btn` : undefined}
                    onClick={() => { setOpen(false); setName(''); setCreating(true); }}
                />
            </SelectContent>
        </Select>
    );
}
